import {
  getPollReport,
  saveDraft,
  save,
  getReceivers,
  getDraft,
  getPollGroups,
  getPollList,
  patchCloseCustomPoll,
  deleteDraft,
  duplicateCustomPoll,
} from '@/service/customPoll'

function inicialReceivers() {
  return {
    viewers: [],
    repliersCount: 0,
  }
}

function initialCustomPoll() {
  return {
    step: 1,
    customPoll: {
      title: '',
      description: '',
      access_level: '',
      company: false,
      sender: 'owner',
      groups: [],
      viewers: [],
      questions: [
        {
          isValid: false,
          question_text: undefined,
          question_type: undefined,
        },
      ],
    },
  }
}

function formatReport(report) {
  const formatQuestion = q => {
    const getAnswers = item =>
      item.question_type === 'paragraph' ? item.feedback_answers : item.answers
    const getFeedbacks = item =>
      item.question_type === 'paragraph' ? [] : item.feedback_answers

    return {
      type: q.question_type,
      text: q.question_text,
      uniqueAnswers: q.num_unique_answers,
      skipped: q.skipped_answers,
      score: q.score,
      answers: getAnswers(q) || [],
      feedback: {
        text: q.feedback_question || null,
        answers: getFeedbacks(q),
      },
    }
  }

  return {
    accessLevel: report.access_level,
    title: report.title,
    createdAt: report.created_date,
    numEmployees: report.num_employees,
    numQuestions: report.num_questions,
    owner: {
      name: report.creator_name,
      email: report.creator_email,
    },
    sender: report.sender,
    stats: report.poll_stats,
    status: report.status,
    questions: report.questions.map(formatQuestion),
  }
}

function initialState() {
  return {
    loading: false,
    poll: initialCustomPoll(),
    report: {},
    groups: [],
    receivers: inicialReceivers(),
    polls: initialPoll(),
    currentCountPolls: 0,
    currentOwner: 'my',
    currentStatus: 'active',
  }
}

const initialPoll = function() {
  return {
    my: {
      active: [],
      drafts: [],
      closed: [],
      onGoing: [],
      'onGoing/done': [],
    },
    others: {
      active: [],
      closed: [],
    },
  }
}

const state = initialState()

const getters = {
  loading: state => state.loading,
  polls: state => state.polls[state.currentOwner][state.currentStatus],
  currentCountPolls: state => state.currentCountPolls,
  report: state => state.report,
  groups: state => state.groups,
  poll: state => state.poll,
  receivers: state => state.receivers,
  pollStep: state => state.poll.step,
  newCustomPoll: state => state.poll.customPoll,
}

const mutations = {
  SET_REPORT: (state, report) => (state.report = report),
  SET_POLL_GROUPS: (state, groups) =>
    (state.groups = Object.assign({}, groups)),
  SET_RECEIVERS: (state, receivers) => (state.receivers = receivers),
  RESET_RECEIVERS: state => (state.receivers = inicialReceivers()),
  SET_POLL_DRAFT_ID: (state, id) => (state.poll.customPoll.draft_id = id),
  RESET_CUSTOM_POLL: state => (state.poll = initialCustomPoll()),
  NEW_CUSTOM_POLL_NEXT_STEP: state => state.poll.step++,
  NEW_CUSTOM_POLL_PREVIOUS_STEP: state => state.poll.step--,
  UPDATE_CUSTOM_POLL_ATTR: (state, properties) =>
    (state.poll.customPoll[properties.property] = properties.value),
  MERGE_NEW_POLL_STATE: (state, properties) =>
    Object.assign((state.poll.customPoll = properties)),
  SET_POLL_LIST_ITEM: (state, value) => {
    state.loading = false
    state.polls[state.currentOwner][state.currentStatus] = value
  },
  RESET_REPORTS: state => {
    state.report = {}
    state.groups = []
  },
  RESET_REPORT: state => {
    state.report = {}
  },
  SET_CURRENT_POLL: (state, { owner, status }) => {
    state.loading = true
    state.currentOwner = owner
    state.currentStatus = status
  },
  SET_LOADING: state => {
    state.loading = true
  },
  SET_QUESTIONS: (state, questions) => {
    state.poll.customPoll.questions = questions
  },
  SET_CURRENT_COUNT_POLLS: (state, metadata) => {
    state['currentCountPolls'] = metadata ? metadata.total : 0
  },
}

const actions = {
  loadPollList({ commit }, { owner, status, limit, offset }) {
    commit('SET_CURRENT_POLL', { owner, status })
    getPollList(owner, status, limit, offset).then(res => {
      commit('SET_POLL_LIST_ITEM', res.data.polls_summary)
      commit('SET_CURRENT_COUNT_POLLS', res.data.metadata)
    })
  },

  closeCustomPoll({ state, commit }, id) {
    commit('SET_LOADING')
    patchCloseCustomPoll(id).then(() => {
      getPollList(state.currentOwner, state.currentStatus).then(res => {
        commit('SET_POLL_LIST_ITEM', res.data.polls_summary)
      })
    })
  },

  closeDraft({ state, commit }, id) {
    commit('SET_LOADING')
    deleteDraft(id).then(() => {
      getPollList(state.currentOwner, state.currentStatus).then(res => {
        commit('SET_POLL_LIST_ITEM', res.data.polls_summary)
      })
    })
  },

  duplicateCustomPoll({ commit }, id) {
    commit('SET_LOADING')
    return duplicateCustomPoll(id)
  },

  getPollReport({ commit }, { pollId, groupId }) {
    commit('RESET_REPORT')
    getPollReport(pollId, groupId).then(r =>
      commit('SET_REPORT', formatReport(r.data.poll_report))
    )
  },

  getPollGroups({ commit }, { pollId }) {
    getPollGroups(pollId).then(r => {
      commit(
        'SET_POLL_GROUPS',
        r.data.poll_groups.map(g => {
          return { name: g.name || g, id: g._id || g }
        })
      )
    })
  },

  getReceivers({ commit }, { accessLevel, company, groups }) {
    return getReceivers(accessLevel, company, groups).then(r =>
      commit('SET_RECEIVERS', r.data)
    )
  },

  getDraft({ commit }, { id }) {
    return getDraft(id).then(r => {
      commit('MERGE_NEW_POLL_STATE', r.data)
      commit('SET_POLL_DRAFT_ID', r.data.draft_id)
    })
  },

  saveDraft({ commit, state }) {
    return saveDraft(state.poll.customPoll).then(r => {
      if (r.data.draft_id) commit('SET_POLL_DRAFT_ID', r.data.draft_id)
    })
  },

  setQuestions({ commit }, questions) {
    commit('SET_QUESTIONS', questions)
  },

  save({ state }) {
    let customPoll = Object.assign(
      { status: 'active' },
      JSON.parse(JSON.stringify(state.poll.customPoll))
    )
    customPoll.groups_input = customPoll.groups.map(group => group._id)
    delete customPoll['groups']
    delete customPoll['creator_email']
    return save(customPoll)
  },

  resetReports({ commit }) {
    commit('RESET_REPORTS')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions,
}
